import type { HarmonyPackageBoard } from '@rhythmic/db/types'
import { KanbanContextSchema, UpdateKanbanBoardSchema } from '@rhythmic/types'
import { useRhythmicLogger } from '#imports'

import { getBoardsCollection } from '../getCollections'

const logger = useRhythmicLogger('useKanbanBoardContext:updateBoard')

export interface UpdateKanbanBoardPayload {
  name?: string
  image?: string | null
  context?: string | null
  iteration_id?: string | null
  publish_status?: string | null
  forecast_start_date?: string | null
  forecast_end_Date?: string | null
  forecast_burndown?: number[] | null
  integrations_map?: Record<string, unknown>[] | null
  cost_per_iteration?: string | null
}

function validateParams(
  boardId: string,
  payload: UpdateKanbanBoardPayload,
  context: {
    boardId: string
    packageId: string
    workspaceId: string
    teamId: string
  },
) {
  if (!context) {
    logger.error('Attempted to update column without an active kanban context')
    throw new Error('Kanban board context is not ready: missing context for updating a column')
  }

  if (!boardId) throw new Error('Board identifier is required to update a kanban board')

  const parsedContext = KanbanContextSchema.parse(context)
  const parsedPayload = UpdateKanbanBoardSchema.parse(payload)

  const { packageId: planId } = context

  if (!planId) throw new Error('Package identifier is required to update a board')
  return { parsedContext, updates: parsedPayload }
}

export default function handleUpdateBoard(
  boardId: string,
  payload: UpdateKanbanBoardPayload,
  context: {
    boardId: string
    packageId: string
    workspaceId: string
    teamId: string
  },
) {
  logger.debug('handleUpdateBoard called', { boardId, payload, context })
  try {
    const { parsedContext, updates } = validateParams(boardId, payload, context)
    const collection = getBoardsCollection(parsedContext.packageId)
    const result = collection.update(boardId, { optimistic: false }, (draft: HarmonyPackageBoard) => {
      Object.assign(draft, updates)
    })
    logger.info('Harmony plan board updated', { parsedContext, boardId })
    return result ?? null
  } catch (error) {
    logger.error('Failed to update harmony plan board', { boardId, error })
    if (error instanceof Error) throw error

    throw new Error('Failed to update harmony plan board', { cause: error })
  }
}
